import { useRef, useEffect, useCallback } from 'react'
import { useThree } from '@react-three/fiber'
import { MathUtils } from 'three'

const SENSITIVITY = 0.0008
const DRAG_ROTATE_LAMBDA = 4
const TARGET_ASPECT = 16 / 9
const VERTICAL_ASPECT_INFLUENCE = 0.5

const DEG2RAD = Math.PI / 180
const DEFAULT_MAX_YAW = 45 * DEG2RAD
const DEFAULT_MAX_PITCH = 20 * DEG2RAD

export function useDragRotate(enabled: boolean, maxR?: number) {
  const { gl, size } = useThree()
  const targetYaw = useRef(0)
  const targetPitch = useRef(0)
  const currentYaw = useRef(0)
  const currentPitch = useRef(0)
  const dragging = useRef(false)
  const lastPointer = useRef({ x: 0, y: 0 })

  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const maxYawRef = useRef(DEFAULT_MAX_YAW)
  const maxPitchRef = useRef(DEFAULT_MAX_PITCH)
  maxYawRef.current = maxR != null ? maxR * DEG2RAD : DEFAULT_MAX_YAW
  maxPitchRef.current = maxR != null ? Math.min(maxR, 20) * DEG2RAD : DEFAULT_MAX_PITCH

  const aspectModifier = TARGET_ASPECT / (size.width / size.height)
  const horizontalSensitivityRef = useRef(SENSITIVITY)
  const verticalSensitivityRef = useRef(SENSITIVITY)
  horizontalSensitivityRef.current = SENSITIVITY * aspectModifier
  verticalSensitivityRef.current = SENSITIVITY * (1 + (aspectModifier - 1) * VERTICAL_ASPECT_INFLUENCE)

  const onPointerDown = useCallback((e: PointerEvent) => {
    if (!enabledRef.current) return
    dragging.current = true
    lastPointer.current = { x: e.clientX, y: e.clientY }
  }, [])

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - lastPointer.current.x
    const dy = e.clientY - lastPointer.current.y
    lastPointer.current = { x: e.clientX, y: e.clientY }
    const maxYaw = maxYawRef.current
    const maxPitch = maxPitchRef.current
    targetYaw.current += dx * horizontalSensitivityRef.current
    targetPitch.current += dy * verticalSensitivityRef.current
    if (maxYaw < Math.PI * 2) {
      targetYaw.current = Math.max(-maxYaw, Math.min(maxYaw, targetYaw.current))
    }
    if (maxPitch < Math.PI * 2) {
      targetPitch.current = Math.max(-maxPitch, Math.min(maxPitch, targetPitch.current))
    }
  }, [])

  const onPointerUp = useCallback(() => {
    dragging.current = false
  }, [])

  useEffect(() => {
    const el = gl.domElement
    el.addEventListener('pointerdown', onPointerDown)
    // Move/up are bound to window so the drag keeps tracking even when the
    // cursor passes over HTML overlays (e.g. interactable markers) or leaves
    // the canvas entirely.
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [gl, onPointerDown, onPointerMove, onPointerUp])

  function update(delta: number) {
    currentYaw.current = MathUtils.damp(currentYaw.current, targetYaw.current, DRAG_ROTATE_LAMBDA, delta)
    currentPitch.current = MathUtils.damp(currentPitch.current, targetPitch.current, DRAG_ROTATE_LAMBDA, delta)
    return { yaw: currentYaw.current, pitch: currentPitch.current }
  }

  function reset() {
    targetYaw.current = 0
    targetPitch.current = 0
    currentYaw.current = 0
    currentPitch.current = 0
  }

  return { update, reset }
}
